import { useEffect, useState } from "react";
import api, { API, nomeCompleto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, RefreshCw, BarChart3, ChevronDown, ChevronUp, Clock, Users, UserCheck, UserX, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FLAG_LABELS = {
  presente_senza_accesso: "Marcato presente, mai collegato",
  accesso_ma_assente: "Collegato ma marcato assente",
  accesso_parziale: "Collegato < 50% della lezione",
};

export const fmtDurata = (v, unit = "h") => {
  if (v == null || isNaN(v)) return "—";
  const tot = Math.round(unit === "min" ? v : v * 60);
  return `${Math.floor(tot / 60)}:${String(tot % 60).padStart(2, "0")}`;
};

export default function PresenzePage() {
  const { user } = useAuth();
  const [classi, setClassi] = useState([]);
  const [selClasse, setSelClasse] = useState("");
  const [lezioni, setLezioni] = useState([]);
  const [selLezione, setSelLezione] = useState("");
  const [disc, setDisc] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [expDal, setExpDal] = useState("");
  const [expAl, setExpAl] = useState("");
  const [showReport, setShowReport] = useState(false);
  const admin = user?.role === "admin";
  const manager = admin || user?.role === "collaboratore";
  const [repDal, setRepDal] = useState("");
  const [repAl, setRepAl] = useState("");
  const [repClasse, setRepClasse] = useState("");
  const [repDocente, setRepDocente] = useState("");
  const [repStudente, setRepStudente] = useState("");
  const [report, setReport] = useState(null);
  const [reportAccessi, setReportAccessi] = useState(null);
  const [reportUnico, setReportUnico] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [docentiUsers, setDocentiUsers] = useState([]);
  const [studentiUsers, setStudentiUsers] = useState([]);
  const canEdit = user?.role === "admin" || user?.role === "docente" || user?.role === "collaboratore";

  const calcolaReport = async () => {
    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    setReportLoading(true);
    try {
      const params = new URLSearchParams({ dal: repDal, al: repAl });
      if (repClasse) params.set("classe_id", repClasse);
      if (repDocente) params.set("docente_id", repDocente);
      if (repStudente) params.set("studente_id", repStudente);
      const { data } = await api.get(`/report/ore?${params}`);
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore report"); }
    finally { setReportLoading(false); }
  };

  const calcolaAccessi = async () => {
    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    try {
      const params = new URLSearchParams({ dal: repDal, al: repAl });
      if (repClasse) params.set("classe_id", repClasse);
      if (repDocente) params.set("docente_id", repDocente);
      if (repStudente) params.set("studente_id", repStudente);
      const { data } = await api.get(`/report/accessi-software?${params}`);
      setReportAccessi(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore report accessi"); }
  };
  const calcolaUnico = async () => {
    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    try {
      const params = new URLSearchParams({ dal: repDal, al: repAl });
      if (repClasse) params.set("classe_id", repClasse);
      if (repStudente) params.set("studente_id", repStudente);
      const { data } = await api.get(`/report/presenze-unico?${params}`);
      setReportUnico(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore report unico"); }
  };
  const exportUnico = async () => {
    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    try {
      const params = new URLSearchParams({ dal: repDal, al: repAl });
      if (repClasse) params.set("classe_id", repClasse);
      if (repStudente) params.set("studente_id", repStudente);
      const res = await api.get(`/report/presenze-unico/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `presenze_unico_${repDal}_${repAl}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore esportazione Excel"); }
  };
  const exportAccessi = async () => {
    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    try {
      const params = new URLSearchParams({ dal: repDal, al: repAl });
      if (repClasse) params.set("classe_id", repClasse);
      if (repDocente) params.set("docente_id", repDocente);
      if (repStudente) params.set("studente_id", repStudente);
      const res = await api.get(`/report/accessi-software/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `accessi_software_${repDal}_${repAl}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore esportazione Excel"); }
  };

  const exportOre = async (tipo) => {    if (!repDal || !repAl) { toast.error("Seleziona il periodo (dal / al)"); return; }
    const token = localStorage.getItem("isteform_token");
    const params = new URLSearchParams({ dal: repDal, al: repAl, tipo });
    if (repClasse) params.set("classe_id", repClasse);
    if (repDocente) params.set("docente_id", repDocente);
    if (repStudente) params.set("studente_id", repStudente);
    try {
      const r = await fetch(`${API}/report/ore/export-excel?${params}`, { credentials:"include", headers: token ? { Authorization:`Bearer ${token}` } : {} });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `report_ore_${tipo}_${repDal}_${repAl}.xlsx`;
      link.click();
      toast.success("Export completato");
    } catch { toast.error("Errore export"); }
  };

  useEffect(() => {
    api.get("/classi").then(r => {
      setClassi(r.data);
      if (!admin && user?.role === "collaboratore") {
        const dmap = {}, smap = {};
        r.data.forEach(c => {
          (c.docenti_dettagli || []).forEach(d => { dmap[d.id] = { id: d.id, name: d.nome }; });
          (c.studenti_dettagli || []).forEach(s => { smap[s.id] = { id: s.id, name: s.nome }; });
        });
        setDocentiUsers(Object.values(dmap));
        setStudentiUsers(Object.values(smap));
      }
    });
    if (admin) {
      api.get("/users?role=docente").then(r=>setDocentiUsers(r.data)).catch(()=>{});
      api.get("/users?role=studente").then(r=>setStudentiUsers(r.data)).catch(()=>{});
    }
  }, [admin, user?.role]);
  useEffect(() => {
    if (!selClasse) return;
    setSelLezione(""); setDisc(null);
    api.get(`/lezioni?classe_id=${selClasse}`).then(r=>setLezioni(r.data));
  }, [selClasse]);
  useEffect(() => {
    if (!selLezione) return;
    api.get(`/lezioni/${selLezione}/discordanze`).then(r => setDisc(r.data)).catch(() => setDisc(null));
  }, [selLezione]);

  const lezSel = lezioni.find(l => l.id === selLezione);
  const syncBbb = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/lezioni/${selLezione}/sync-bbb`);
      const r = data.risultato || {};
      if (r.ok) toast.success(`Sincronizzato: ${r.partecipanti ?? 0} partecipanti online${r.finalizzato ? " — registro aggiornato" : ""}`);
      else toast.info(`Server BBB: ${r.motivo || "nessun dato"}`);
      const dr = await api.get(`/lezioni/${selLezione}/discordanze`);
      setDisc(dr.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore sincronizzazione BBB"); }
    finally { setSyncing(false); }
  };

  const exportXlsx = async () => {
    const token = localStorage.getItem("isteform_token");
    const params = new URLSearchParams({ classe_id: selClasse });
    if (expDal) params.set("dal", expDal);
    if (expAl) params.set("al", expAl);
    const url = `${API}/presenze/export/csv?${params.toString()}`;
    try {
      const r = await fetch(url, { credentials:"include", headers: token ? { Authorization:`Bearer ${token}` } : {} });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const suffix = expDal || expAl ? `_${expDal || "inizio"}_${expAl || "oggi"}` : "";
      link.download = `registro_presenze_${selClasse}${suffix}.xlsx`;
      link.click();
      toast.success("Export completato");
    } catch { toast.error("Errore export"); }
  };

  const studs = disc?.studenti || [];
  const nPresenti = studs.filter(s => s.marcato_presente === true).length;
  const nAssenti = studs.filter(s => s.marcato_presente === false).length;
  const nAnomalie = studs.filter(s => s.discordanze?.length).length;

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Didattica</div>
        <h1 className="text-3xl font-bold text-stone-900">Registro Presenze</h1>
        <p className="text-stone-600 mt-1">Presenze e accessi tracciati automaticamente da BigBlueButton · Export Excel · Report ore</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="min-w-[280px]">
          <Select value={selClasse} onValueChange={setSelClasse}>
            <SelectTrigger data-testid="presenze-classe-select"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
            <SelectContent>{classi.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[280px]">
          <Select value={selLezione} onValueChange={setSelLezione} disabled={!selClasse}>
            <SelectTrigger data-testid="presenze-lezione-select"><SelectValue placeholder="Seleziona lezione"/></SelectTrigger>
            <SelectContent>{lezioni.map(l=><SelectItem key={l.id} value={l.id}>{l.data} · {l.ora_inizio}–{l.ora_fine} · {l.titolo}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selClasse && canEdit && (
          <div className="flex flex-wrap items-center gap-2 border border-[#E2E8F0] rounded-lg px-3 py-1.5 bg-white">
            <span className="text-xs text-stone-500">Periodo export:</span>
            <Input type="date" value={expDal} onChange={e=>setExpDal(e.target.value)} className="w-36 h-8" data-testid="export-presenze-dal"/>
            <span className="text-xs text-stone-400">→</span>
            <Input type="date" value={expAl} onChange={e=>setExpAl(e.target.value)} className="w-36 h-8" data-testid="export-presenze-al"/>
            {(expDal || expAl) && <button onClick={()=>{setExpDal("");setExpAl("");}} className="text-xs text-[#F7941E] hover:underline" data-testid="export-presenze-reset">azzera</button>}
            <Button onClick={exportXlsx} variant="outline" className="border-[#F7941E] text-[#F7941E] h-8" data-testid="export-presenze-btn">
              <Download className="w-4 h-4 mr-2"/> Export Registro Excel
            </Button>
          </div>
        )}
        {lezSel?.bbb_link && canEdit && (
          <Button onClick={syncBbb} variant="outline" disabled={syncing} className="border-emerald-700 text-emerald-700" data-testid="sync-bbb-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}/> Sincronizza da BBB
          </Button>
        )}
      </div>

      {selLezione && disc && (
        <Card className="p-5 mb-6 bg-white border-[#E2E8F0]" data-testid="discordanze-card">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-semibold text-stone-900 text-lg">{lezSel?.titolo}</div>
              <div className="text-sm text-stone-500 flex items-center gap-1.5 mt-0.5">
                <Clock className="w-3.5 h-3.5"/> {lezSel?.data} · {lezSel?.ora_inizio}–{lezSel?.ora_fine}
                {disc.durata_prevista_min ? <span className="text-stone-400">· durata prevista {fmtDurata(disc.durata_prevista_min, "min")}</span> : null}
              </div>
            </div>
            <div className="flex gap-2 text-xs">
              <Badge variant="outline" className="gap-1 border-stone-300 text-stone-700" data-testid="stat-totale"><Users className="w-3 h-3"/> {studs.length} iscritti</Badge>
              <Badge className="gap-1 bg-emerald-50 text-emerald-800" data-testid="stat-presenti"><UserCheck className="w-3 h-3"/> {nPresenti} presenti</Badge>
              <Badge className="gap-1 bg-red-50 text-red-800" data-testid="stat-assenti"><UserX className="w-3 h-3"/> {nAssenti} assenti</Badge>
              {nAnomalie > 0 && <Badge className="gap-1 bg-amber-50 text-amber-900" data-testid="stat-anomalie"><AlertTriangle className="w-3 h-3"/> {nAnomalie} anomalie</Badge>}
            </div>
          </div>

          {disc.docenti?.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Docente</div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Docente</TableHead><TableHead>Ingresso</TableHead><TableHead>Chiusura</TableHead><TableHead>Collegato</TableHead><TableHead>Ore svolte</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {disc.docenti.map(d => (
                    <TableRow key={d.docente_id} data-testid={`disc-docente-${d.docente_id}`}>
                      <TableCell className="font-medium">{d.nome}</TableCell>
                      {d.accesso_bbb ? <>
                        <TableCell className="font-mono text-sm">{d.ingresso}</TableCell>
                        <TableCell className="font-mono text-sm">{d.chiusura}</TableCell>
                        <TableCell className="font-mono text-sm">{fmtDurata(d.minuti_bbb, "min")}</TableCell>
                      </> : <TableCell colSpan={3}><Badge className="bg-red-50 text-red-800">Nessun accesso BBB rilevato</Badge></TableCell>}
                      <TableCell className="font-mono text-sm text-emerald-700">{d.ore_effettive != null ? fmtDurata(d.ore_effettive) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Studenti</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Studente</TableHead><TableHead>Stato</TableHead>
              <TableHead>Ingresso</TableHead><TableHead>Uscita</TableHead><TableHead>Durata</TableHead>
              <TableHead>Anomalie</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {studs.map(s => (
                <TableRow key={s.studente_id} className={s.discordanze?.length ? "bg-amber-50/40" : ""} data-testid={`disc-studente-${s.studente_id}`}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell>
                    {s.marcato_presente === true && <Badge className="bg-emerald-50 text-emerald-800">Presente</Badge>}
                    {s.marcato_presente === false && <Badge className="bg-red-50 text-red-800">Assente</Badge>}
                    {s.marcato_presente == null && <span className="text-xs text-stone-400">non registrato</span>}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{s.accesso_bbb ? s.ingresso : <span className="text-stone-300">—</span>}</TableCell>
                  <TableCell className="font-mono text-sm">{s.accesso_bbb ? s.uscita : <span className="text-stone-300">—</span>}</TableCell>
                  <TableCell className="font-mono text-sm" data-testid={`presenza-durata-${s.studente_id}`}>{s.accesso_bbb ? fmtDurata(s.minuti, "min") : <span className="text-stone-300">—</span>}</TableCell>
                  <TableCell className="space-x-1">
                    {s.discordanze?.length ? s.discordanze.map(f => <Badge key={f} className="bg-amber-50 text-amber-900" data-testid={`flag-${f}-${s.studente_id}`}>{FLAG_LABELS[f] || f}</Badge>) : <span className="text-xs text-stone-300">—</span>}
                  </TableCell>
                </TableRow>
              ))}
              {studs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500">Nessuno studente in classe.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
      {!selLezione && (
        <div className="text-stone-500 text-center py-12 border border-dashed border-stone-200 rounded-xl mb-6">Seleziona una classe e una lezione per vedere il registro.</div>
      )}

      <Card className="bg-white border-[#E2E8F0]" data-testid="report-ore-card">
        <button type="button" onClick={() => setShowReport(v => !v)} className="w-full flex items-center justify-between p-5 text-left" data-testid="report-toggle-btn">
          <div>
            <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#F7941E]"/><span className="font-semibold text-stone-900">Estrapolazione ore per periodo</span></div>
            <div className="text-xs text-stone-500 mt-1">Filtra per periodo e, a scelta, per classe{manager ? ", docente o studente" : ""}{!manager && " — visibilità limitata alle tue classi e alle tue ore"}{manager && !admin && " — dati limitati alle tue classi"}</div>
          </div>
          {showReport ? <ChevronUp className="w-5 h-5 text-stone-400"/> : <ChevronDown className="w-5 h-5 text-stone-400"/>}
        </button>
        {showReport && <div className="px-5 pb-5">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div><div className="text-xs text-stone-500 mb-1">Dal</div><Input type="date" value={repDal} onChange={e=>setRepDal(e.target.value)} data-testid="report-dal"/></div>
            <div><div className="text-xs text-stone-500 mb-1">Al</div><Input type="date" value={repAl} onChange={e=>setRepAl(e.target.value)} data-testid="report-al"/></div>
            <div className="min-w-[180px]"><div className="text-xs text-stone-500 mb-1">Classe</div>
              <Select value={repClasse} onValueChange={setRepClasse}><SelectTrigger data-testid="report-classe"><SelectValue placeholder="Tutte"/></SelectTrigger>
                <SelectContent>{classi.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
            {manager && <>
              <div className="min-w-[180px]"><div className="text-xs text-stone-500 mb-1">Docente</div>
                <Select value={repDocente} onValueChange={setRepDocente}><SelectTrigger data-testid="report-docente"><SelectValue placeholder="Tutti"/></SelectTrigger>
                  <SelectContent>{docentiUsers.map(d=><SelectItem key={d.id} value={d.id}>{nomeCompleto(d)}</SelectItem>)}</SelectContent></Select></div>
              <div className="min-w-[180px]"><div className="text-xs text-stone-500 mb-1">Studente</div>
                <Select value={repStudente} onValueChange={setRepStudente}><SelectTrigger data-testid="report-studente"><SelectValue placeholder="Tutti"/></SelectTrigger>
                  <SelectContent>{studentiUsers.map(s=><SelectItem key={s.id} value={s.id}>{nomeCompleto(s)}</SelectItem>)}</SelectContent></Select></div>
            </>}
            <Button onClick={calcolaReport} disabled={reportLoading} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="report-calcola-btn">{reportLoading ? "Calcolo..." : "Calcola"}</Button>
            <Button onClick={calcolaAccessi} variant="outline" className="border-[#1F4E79] text-[#1F4E79]" data-testid="report-accessi-btn">Accessi software</Button>
            <Button onClick={calcolaUnico} variant="outline" className="border-emerald-700 text-emerald-700" data-testid="report-unico-btn">Presenze unico</Button>
            <Button onClick={()=>exportOre("docenti")} variant="outline" className="border-[#1F4E79] text-[#1F4E79]" data-testid="export-ore-docenti">Esporta ore docenti</Button>
            <Button onClick={()=>exportOre("studenti")} variant="outline" className="border-emerald-700 text-emerald-700" data-testid="export-ore-alunni">Esporta ore alunni</Button>
          </div>
          {reportAccessi && (
            <div className="mb-4" data-testid="report-accessi-risultati">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Accessi al software (minuti di collegamento) · {reportAccessi.dal} → {reportAccessi.al}</div>
                <Button size="sm" variant="outline" onClick={exportAccessi} data-testid="report-accessi-export-btn">Esporta Excel</Button>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Utente</TableHead><TableHead>Ruolo</TableHead><TableHead>Ore (h:mm)</TableHead><TableHead>Minuti</TableHead></TableRow></TableHeader>
                <TableBody>
                  {reportAccessi.utenti.map(u => (
                    <TableRow key={u.user_id} data-testid={`accessi-row-${u.user_id}`}>
                      <TableCell className="font-medium">{u.nome}</TableCell><TableCell className="text-sm">{u.ruolo}</TableCell>
                      <TableCell className="font-mono">{fmtDurata(u.ore)}</TableCell><TableCell className="font-mono">{u.minuti}</TableCell>
                    </TableRow>
                  ))}
                  {reportAccessi.utenti.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500">Nessun accesso nel periodo</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          {reportUnico && (
            <div className="mb-4" data-testid="report-unico-risultati">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">Presenza unica (ore BBB validate + software fuori BBB, senza doppio conteggio) · {reportUnico.dal} → {reportUnico.al}</div>
                <Button size="sm" variant="outline" onClick={exportUnico} data-testid="report-unico-export-btn">Esporta Excel</Button>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Studente</TableHead><TableHead>Ore BBB</TableHead><TableHead>Ore software</TableHead><TableHead>Presenza effettiva</TableHead></TableRow></TableHeader>
                <TableBody>
                  {reportUnico.studenti.map(s => (
                    <TableRow key={s.studente_id} data-testid={`unico-row-${s.studente_id}`}>
                      <TableCell className="font-medium">{s.nome}</TableCell>
                      <TableCell className="font-mono">{fmtDurata(s.ore_bbb)}</TableCell>
                      <TableCell className="font-mono">{fmtDurata(s.ore_software)}</TableCell>
                      <TableCell className="font-mono font-bold text-emerald-700">{fmtDurata(s.presenza_effettiva)}</TableCell>
                    </TableRow>
                  ))}
                  {reportUnico.studenti.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500">Nessun dato nel periodo</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          {report && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="report-risultati">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Studenti · {report.lezioni_totali} lezioni nel periodo {report.dal} → {report.al}</div>
                <Table>
                  <TableHeader><TableRow><TableHead>Studente</TableHead><TableHead>Ore (h:mm)</TableHead><TableHead>Presenze</TableHead><TableHead>Assenze</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.studenti.map(s => (
                      <TableRow key={s.studente_id} data-testid={`report-studente-row-${s.studente_id}`}>
                        <TableCell className="font-medium">{s.nome}</TableCell>
                        <TableCell className="font-mono">{fmtDurata(s.ore)}</TableCell>
                        <TableCell>{s.presenze}</TableCell><TableCell>{s.assenze}</TableCell>
                      </TableRow>
                    ))}
                    {report.studenti.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500">Nessun dato nel periodo</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Docenti (ore da tracking BBB)</div>
                <Table>
                  <TableHeader><TableRow><TableHead>Docente</TableHead><TableHead>Ore effettive (h:mm)</TableHead><TableHead>Lezioni</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.docenti.map(d => (
                      <TableRow key={d.docente_id} data-testid={`report-docente-row-${d.docente_id}`}>
                        <TableCell className="font-medium">{d.nome}</TableCell>
                        <TableCell className="font-mono">{fmtDurata(d.ore)}</TableCell>
                        <TableCell>{d.lezioni}</TableCell>
                      </TableRow>
                    ))}
                    {report.docenti.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-stone-500">Nessun dato nel periodo</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>}
      </Card>
    </div>
  );
}
